import { useEffect, useState } from 'react'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { DropzoneField } from '@components/form/dropzone'
import { docs } from '@services/docs/docs-service'
import { useNavigate } from 'react-router-dom'
import { AnalyticsService } from '@/services/analytics'
import { DocumentStatus } from '@interfaces/document'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { LoaderCircle } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import Container from '@/components/common/container'
import Breadcrumb from '@/components/common/breadcrumb'
import { user as userService } from '@services/user/user-service'
import { useBilling } from '@/hooks/useBilling'

type FormInputs = {
  file: FileList
}

const Upload = () => {
  document.title = 'Upload'

  const { trialingAndCappedDocs, onSubscribe } = useBilling()

  const [loading, setLoading] = useState(false)
  const [buttonText, setButtonText] = useState('Uploading...')
  const [error, setError] = useState<string>()
  const [fileId, setFileId] = useState<string>()

  const methods = useForm<FormInputs>()
  const { watch, handleSubmit } = methods
  const fileValue = watch('file')

  const navigate = useNavigate()

  const fetchStatusByPolling = async (fileId: string) => {
    const response = await docs.getStatusByPublicId(fileId)
    if (response.status === 200) {
      const { status } = response.data as DocumentStatus
      if (status !== 'processing') {
        return true
      }
    }
    return false
  }

  const onSubmit: SubmitHandler<FormInputs> = async (data: any) => {
    if (trialingAndCappedDocs) {
      onSubscribe('TRIAL_LIMIT_DOCS_COUNT')
      return
    }

    if (data.file.length === 0) {
      return
    }

    try {
      setLoading(true)
      const formData = new FormData()
      formData.append('file', data.file)
      const response = await docs.upload(formData)
      if (response.status === 200) {
        setButtonText('Processing...')
        setFileId(response.data.tag)
        AnalyticsService.trackDocumentUpload(response.data.tag)
      }
    } catch (e: any) {
      setError(e.response?.data?.message || 'Something went wrong. Please try again.')
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!fileId) return
    setLoading(true)
    const intervalId = setInterval(async () => {
      const isDataReady = await fetchStatusByPolling(fileId)
      if (isDataReady) {
        clearInterval(intervalId)

        // send email sequence
        userService.sendEmail('email-sequence-app', fileId)

        navigate(`/review/${fileId}`)
      }
    }, 2000)

    return () => clearInterval(intervalId)
  }, [fileId])

  const triggerSubmit = () => handleSubmit(onSubmit)()

  return (
    <>
      <Container>
        <Breadcrumb title="Upload" />
        <Card className="m-auto flex w-[480px] flex-col justify-center space-y-4">
          <CardHeader className="px-7">
            <CardTitle>Upload</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="w-full">
              <div className="relative w-full overflow-auto">
                <FormProvider {...methods}>
                  <form onSubmit={methods.handleSubmit(onSubmit)}>
                    {error ? (
                      <Alert variant="destructive" className="mb-4">
                        <AlertDescription>{error}</AlertDescription>
                      </Alert>
                    ) : null}
                    <DropzoneField name="file" multiple={false} onFilesAccepted={triggerSubmit} />
                    <div className="mt-4">
                      <Button className="w-full" type="submit" disabled={loading || !fileValue}>
                        {loading ? (
                          <>
                            <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
                            {buttonText}
                          </>
                        ) : (
                          'Upload and review'
                        )}
                      </Button>
                    </div>
                  </form>
                </FormProvider>
              </div>
            </div>
          </CardContent>
        </Card>
      </Container>
    </>
  )
}

export default Upload
